import { useCallback, useEffect, useRef, useState } from 'react';
import type { Collection, ColorLabel, Keyword, Photo } from '../types';
import { api, ApiError } from '../api';
import { useSession } from '../auth/SessionContext';
import { prepareImage } from './thumbnail';
import { PhotoCard } from './PhotoCard';
import { InfoPanel } from './InfoPanel';
import { HelpTooltip } from '../components/HelpTooltip';
import './Library.css';

interface Filter {
  minRating: number;
  flag: number | null;
  color: ColorLabel | 'any';
  collectionId: string | null;
  keywordId: string | null;
  query: string;
}

const emptyFilter: Filter = {
  minRating: 0,
  flag: null,
  color: 'any',
  collectionId: null,
  keywordId: null,
  query: '',
};

interface Props {
  onOpenEditor: (photo: Photo) => void;
}

export function Library({ onOpenEditor }: Props) {
  const { logout } = useSession();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [filter, setFilter] = useState<Filter>(emptyFilter);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [importing, setImporting] = useState<{ done: number; total: number } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadPhotos = useCallback(async () => {
    const params: Record<string, string> = {};
    if (filter.minRating) params.rating = String(filter.minRating);
    if (filter.flag !== null) params.flag = String(filter.flag);
    if (filter.color !== 'any' && filter.color) params.color = filter.color;
    if (filter.collectionId) params.collection = filter.collectionId;
    if (filter.keywordId) params.keyword = filter.keywordId;
    if (filter.query) params.q = filter.query;
    try {
      setPhotos(await api.listPhotos(params));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load photos');
    }
  }, [filter]);

  const loadSidebar = useCallback(async () => {
    const [c, k] = await Promise.all([
      api.listCollections(),
      api.listKeywords(),
    ]);
    setCollections(c);
    setKeywords(k);
  }, []);

  useEffect(() => {
    void loadPhotos();
  }, [loadPhotos]);
  useEffect(() => {
    void loadSidebar();
  }, [loadSidebar]);

  async function handleFiles(files: FileList | null) {
    if (!files || !files.length) return;
    const list = Array.from(files).filter((f) => f.type.startsWith('image/'));
    setImporting({ done: 0, total: list.length });
    setError(null);
    for (let i = 0; i < list.length; i++) {
      const file = list[i];
      try {
        const prepared = await prepareImage(file);
        const form = new FormData();
        form.append('original', file);
        form.append(
          'thumb',
          new File([prepared.thumbBlob], 'thumb.jpg', { type: 'image/jpeg' }),
        );
        form.append('width', String(prepared.width));
        form.append('height', String(prepared.height));
        await api.uploadPhoto(form);
      } catch (e) {
        setError(
          `Failed to import ${file.name}: ${
            e instanceof Error ? e.message : 'error'
          }`,
        );
      }
      setImporting({ done: i + 1, total: list.length });
    }
    setImporting(null);
    await loadPhotos();
  }

  async function patch(id: string, body: Record<string, unknown>) {
    setPhotos((ps) => ps.map((p) => (p.id === id ? { ...p, ...body } : p)));
    try {
      await api.updatePhoto(id, body);
    } catch {
      void loadPhotos();
    }
  }

  async function createCollection() {
    const name = prompt('Collection name');
    if (!name?.trim()) return;
    await api.createCollection(name.trim());
    await loadSidebar();
  }

  async function deletePhoto(id: string) {
    if (!confirm('Delete this photo permanently?')) return;
    await api.deletePhoto(id);
    setSelectedId(null);
    await loadPhotos();
    await loadSidebar();
  }

  const selected = photos.find((p) => p.id === selectedId) ?? null;

  return (
    <div className="lib">
      <aside className="lib__side">
        <div className="lib__brand">◎ Loulou Pictures</div>

        <button className="lib__import" onClick={() => fileRef.current?.click()}>
          Import photos <HelpTooltip id="import" />
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />

        <nav className="lib__nav">
          <button
            className={`lib__navitem ${!filter.collectionId && !filter.keywordId ? 'is-active' : ''}`}
            onClick={() => setFilter(emptyFilter)}
          >
            All photos
          </button>
        </nav>

        <div className="lib__group">
          <div className="lib__grouphead">
            <span>Collections</span>
            <HelpTooltip id="collections" />
            <button className="lib__add" onClick={createCollection}>
              +
            </button>
          </div>
          {collections.map((c) => (
            <button
              key={c.id}
              className={`lib__navitem ${filter.collectionId === c.id ? 'is-active' : ''}`}
              onClick={() =>
                setFilter({ ...emptyFilter, collectionId: c.id })
              }
            >
              {c.name} <span className="lib__count">{c.count}</span>
            </button>
          ))}
          {!collections.length && <p className="lib__empty">No collections</p>}
        </div>

        <div className="lib__group">
          <div className="lib__grouphead">
            <span>Keywords</span>
            <HelpTooltip id="keywords" />
          </div>
          {keywords.map((k) => (
            <button
              key={k.id}
              className={`lib__navitem ${filter.keywordId === k.id ? 'is-active' : ''}`}
              onClick={() => setFilter({ ...emptyFilter, keywordId: k.id })}
            >
              {k.name} <span className="lib__count">{k.count}</span>
            </button>
          ))}
          {!keywords.length && <p className="lib__empty">No keywords yet</p>}
        </div>

        <button className="lib__logout" onClick={logout}>
          Lock &amp; sign out
        </button>
      </aside>

      <main className="lib__main">
        <header className="lib__toolbar">
          <input
            className="lib__search"
            type="search"
            placeholder="Search filename…"
            value={filter.query}
            onChange={(e) => setFilter((f) => ({ ...f, query: e.target.value }))}
          />

          <label className="lib__filter">
            Rating
            <HelpTooltip id="rating" />
            <select
              value={filter.minRating}
              onChange={(e) =>
                setFilter((f) => ({ ...f, minRating: Number(e.target.value) }))
              }
            >
              <option value={0}>Any</option>
              <option value={1}>≥ 1</option>
              <option value={2}>≥ 2</option>
              <option value={3}>≥ 3</option>
              <option value={4}>≥ 4</option>
              <option value={5}>5</option>
            </select>
          </label>

          <label className="lib__filter">
            Flag
            <HelpTooltip id="flag" />
            <select
              value={filter.flag ?? ''}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  flag: e.target.value === '' ? null : Number(e.target.value),
                }))
              }
            >
              <option value="">Any</option>
              <option value={1}>Picked</option>
              <option value={-1}>Rejected</option>
              <option value={0}>Unflagged</option>
            </select>
          </label>

          <label className="lib__filter">
            Color
            <HelpTooltip id="colorLabel" />
            <select
              value={filter.color ?? 'any'}
              onChange={(e) =>
                setFilter((f) => ({
                  ...f,
                  color: e.target.value as Filter['color'],
                }))
              }
            >
              <option value="any">Any</option>
              <option value="red">Red</option>
              <option value="yellow">Yellow</option>
              <option value="green">Green</option>
              <option value="blue">Blue</option>
              <option value="purple">Purple</option>
            </select>
          </label>

          <span className="lib__spacer" />
          <span className="lib__stat">{photos.length} photos</span>
        </header>

        {error && <p className="lib__error">{error}</p>}
        {importing && (
          <p className="lib__importing">
            Importing… {importing.done}/{importing.total}
          </p>
        )}

        <div className="lib__gridwrap">
          {photos.length === 0 && !importing ? (
            <div className="lib__blank">
              <p>No photos yet.</p>
              <button
                className="chip chip--primary"
                onClick={() => fileRef.current?.click()}
              >
                Import your first photos
              </button>
            </div>
          ) : (
            <div className="lib__grid">
              {photos.map((p) => (
                <PhotoCard
                  key={p.id}
                  photo={p}
                  selected={p.id === selectedId}
                  onSelect={() => setSelectedId(p.id)}
                  onOpen={() => onOpenEditor(p)}
                  onRate={(rating) => patch(p.id, { rating })}
                  onFlag={(flag) => patch(p.id, { flag })}
                  onColor={(colorLabel) => patch(p.id, { colorLabel })}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {selected && (
        <InfoPanel
          photo={selected}
          collections={collections}
          onChanged={() => {
            void loadPhotos();
            void loadSidebar();
          }}
          onOpen={() => onOpenEditor(selected)}
          onDelete={() => deletePhoto(selected.id)}
        />
      )}
    </div>
  );
}
